/**
 * Template Application Wizard
 *
 * Multi-step wizard for configuring and applying HVAC templates.
 */

import { useState, useEffect, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  useTemplateStore,
  useWizardState,
  useSelectedTemplate,
  useParameterValues,
  useZoneSelections,
} from './templateStore';
import { WIZARD_STEPS, TEMPLATE_CATEGORY_INFO } from './types';
import { generateSystemPreview, applyTemplate, getZonesFromIDF } from './templateEngine';
import { useIDFStore } from '../stores/idfStore';

/**
 * Step indicator showing progress through wizard
 */
function StepIndicator() {
  const wizard = useWizardState();
  const getCurrentStepIndex = useTemplateStore((s) => s.getCurrentStepIndex);
  const currentIndex = getCurrentStepIndex();

  return (
    <div className="flex items-center justify-between mb-6">
      {WIZARD_STEPS.map((step, index) => {
        const isActive = step.id === wizard.currentStep;
        const isComplete = index < currentIndex;

        return (
          <div key={step.id} className="flex items-center">
            {/* Step Circle */}
            <div
              className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-colors',
                isActive && 'bg-primary text-primary-foreground',
                isComplete && 'bg-primary/20 text-primary',
                !isActive && !isComplete && 'bg-secondary text-muted-foreground'
              )}
            >
              {isComplete ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                index + 1
              )}
            </div>

            {/* Step Label */}
            <div className="ml-2 hidden sm:block">
              <div className={cn('text-xs font-medium', isActive ? 'text-foreground' : 'text-muted-foreground')}>
                {step.name}
              </div>
            </div>

            {/* Connector Line */}
            {index < WIZARD_STEPS.length - 1 && (
              <div
                className={cn(
                  'w-8 sm:w-16 h-0.5 mx-2',
                  index < currentIndex ? 'bg-primary/40' : 'bg-border'
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Step 1: Select Template (shown in library, this step is just confirmation)
 */
function ConfigureSystemStep() {
  const template = useSelectedTemplate();
  const parameterValues = useParameterValues();
  const setParameterValue = useTemplateStore((s) => s.setParameterValue);

  if (!template) return null;

  // Group parameters
  const parameterGroups = useMemo(() => {
    const groups = new Map<string, typeof template.parameters>();
    for (const param of template.parameters) {
      const groupName = param.group || 'General';
      if (!groups.has(groupName)) {
        groups.set(groupName, []);
      }
      groups.get(groupName)?.push(param);
    }
    return groups;
  }, [template.parameters]);

  return (
    <div className="space-y-6">
      {/* Template Info */}
      <div className="flex items-start gap-4 p-4 bg-secondary/50 rounded-lg">
        <div className="p-3 rounded-md bg-primary/10 text-primary">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div>
        <div>
          <h3 className="font-semibold">{template.name}</h3>
          <p className="text-sm text-muted-foreground">{template.description}</p>
          <div className="mt-2 flex gap-2">
            <span className="inline-flex items-center px-2 py-0.5 text-xs bg-secondary rounded">
              {TEMPLATE_CATEGORY_INFO[template.category].name}
            </span>
          </div>
        </div>
      </div>

      {/* System Name */}
      <div>
        <label className="block text-sm font-medium mb-1.5">System Name</label>
        <input
          type="text"
          placeholder="Enter system name..."
          value={String(parameterValues['systemName'] || '')}
          onChange={(e) => setParameterValue('systemName', e.target.value)}
          className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <p className="mt-1 text-xs text-muted-foreground">
          This name will be used as a prefix for all generated components
        </p>
      </div>

      {/* Parameter Groups */}
      {Array.from(parameterGroups.entries()).map(([groupName, params]) => (
        <div key={groupName} className="space-y-4">
          <h4 className="text-sm font-medium text-muted-foreground border-b pb-2">{groupName}</h4>
          <div className="grid gap-4 sm:grid-cols-2">
            {params.map((param) => (
              <div key={param.id}>
                <label className="block text-sm font-medium mb-1.5">
                  {param.name}
                  {param.validation?.required && <span className="text-destructive ml-1">*</span>}
                </label>

                {param.type === 'select' && param.options && (
                  <select
                    value={String(parameterValues[param.id] ?? param.defaultValue)}
                    onChange={(e) => setParameterValue(param.id, e.target.value)}
                    className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {param.options.map((opt) => (
                      <option key={String(opt.value)} value={String(opt.value)}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                )}

                {param.type === 'number' && (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={Number(parameterValues[param.id] ?? param.defaultValue)}
                      onChange={(e) => setParameterValue(param.id, parseFloat(e.target.value))}
                      min={param.validation?.min}
                      max={param.validation?.max}
                      step="0.1"
                      className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    {param.unit && (
                      <span className="text-sm text-muted-foreground whitespace-nowrap">{param.unit}</span>
                    )}
                  </div>
                )}

                {param.type === 'boolean' && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Boolean(parameterValues[param.id] ?? param.defaultValue)}
                      onChange={(e) => setParameterValue(param.id, e.target.checked)}
                      className="w-4 h-4 rounded border-border"
                    />
                    <span className="text-sm">{param.description}</span>
                  </label>
                )}

                {param.type === 'text' && (
                  <input
                    type="text"
                    value={String(parameterValues[param.id] ?? param.defaultValue)}
                    onChange={(e) => setParameterValue(param.id, e.target.value)}
                    className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                )}

                {param.type !== 'boolean' && param.description && (
                  <p className="mt-1 text-xs text-muted-foreground">{param.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Step 2: Assign Zones
 */
function AssignZonesStep() {
  const template = useSelectedTemplate();
  const zoneSelections = useZoneSelections();
  const idfDocument = useIDFStore((s) => s.document);
  const setAvailableZones = useTemplateStore((s) => s.setAvailableZones);
  const toggleZoneSelection = useTemplateStore((s) => s.toggleZoneSelection);
  const selectAllZones = useTemplateStore((s) => s.selectAllZones);
  const deselectAllZones = useTemplateStore((s) => s.deselectAllZones);
  const setZoneTerminalType = useTemplateStore((s) => s.setZoneTerminalType);

  // Load zones from IDF
  useEffect(() => {
    if (idfDocument) {
      const zones = getZonesFromIDF(idfDocument);
      setAvailableZones(zones);
    }
  }, [idfDocument, setAvailableZones]);

  if (!template) return null;

  const selectedCount = zoneSelections.filter((z) => z.selected).length;
  const hasMultipleTerminalTypes = template.terminalTypes && template.terminalTypes.length > 1;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium">Select Zones</h3>
          <p className="text-sm text-muted-foreground">
            Choose which zones this system will serve
            {template.minZones && template.maxZones && (
              <span className="ml-1">
                ({template.minZones} - {template.maxZones} zones)
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={selectAllZones}>
            Select All
          </Button>
          <Button variant="outline" size="sm" onClick={deselectAllZones}>
            Clear
          </Button>
        </div>
      </div>

      {/* Zone List */}
      {zoneSelections.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
          <p className="text-sm">No zones found in the model</p>
          <p className="text-xs mt-1">Add zones to your IDF file first</p>
        </div>
      ) : (
        <div className="border rounded-lg divide-y max-h-80 overflow-auto">
          {zoneSelections.map((zone) => (
            <div
              key={zone.zoneId}
              className={cn(
                'flex items-center justify-between p-3 hover:bg-secondary/50 transition-colors',
                zone.selected && 'bg-primary/5'
              )}
            >
              <label className="flex items-center gap-3 flex-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={zone.selected}
                  onChange={() => toggleZoneSelection(zone.zoneId)}
                  className="w-4 h-4 rounded border-border"
                />
                <span className="text-sm font-medium">{zone.zoneName}</span>
              </label>

              {/* Terminal Type Selector */}
              {zone.selected && hasMultipleTerminalTypes && template.terminalTypes && (
                <select
                  value={zone.terminalType || template.terminalTypes[0]?.componentType}
                  onChange={(e) => setZoneTerminalType(zone.zoneId, e.target.value)}
                  className="text-xs px-2 py-1 border rounded bg-background"
                >
                  {template.terminalTypes.map((tt) => (
                    <option key={tt.componentType} value={tt.componentType}>
                      {tt.hasReheat ? 'With Reheat' : 'No Reheat'}
                    </option>
                  ))}
                </select>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Selection Summary */}
      <div className="text-sm text-muted-foreground">
        {selectedCount} zone{selectedCount !== 1 ? 's' : ''} selected
      </div>
    </div>
  );
}

/**
 * Step 3: Plant Loops Configuration
 */
function PlantLoopsStep() {
  const template = useSelectedTemplate();
  const parameterValues = useParameterValues();

  if (!template || !template.plantLoops || template.plantLoops.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p className="text-sm">No plant loops required</p>
        <p className="text-xs mt-1">This template does not create any plant loops</p>
      </div>
    );
  }

  // Evaluate which loops will be created based on conditions
  const activeLoops = template.plantLoops.filter((loop) => {
    if (!loop.conditional) return true;
    // Simple evaluation - in production would use proper expression parser
    try {
      const fn = new Function('parameters', `return ${loop.conditional.replace(/parameters\.(\w+)/g, 'parameters["$1"]')};`);
      return Boolean(fn(parameterValues));
    } catch {
      return true;
    }
  });

  const loopTypeNames: Record<string, string> = {
    'chilled-water': 'Chilled Water Loop',
    'hot-water': 'Hot Water Loop',
    'condenser-water': 'Condenser Water Loop',
    'ground-loop': 'Ground Loop',
    'service-hot-water': 'Service Hot Water Loop',
  };

  const equipmentNames: Record<string, string> = {
    'boiler-hot-water': 'Hot Water Boiler',
    'chiller-electric': 'Electric Chiller',
    'cooling-tower': 'Cooling Tower',
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-medium">Plant Loops</h3>
        <p className="text-sm text-muted-foreground">
          The following plant loops will be created automatically
        </p>
      </div>

      {activeLoops.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground border rounded-lg">
          <p className="text-sm">No plant loops will be created based on current configuration</p>
        </div>
      ) : (
        <div className="space-y-3">
          {activeLoops.map((loop, index) => (
            <div key={index} className="border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    'w-3 h-3 rounded-full',
                    loop.type === 'chilled-water' && 'bg-blue-500',
                    loop.type === 'hot-water' && 'bg-red-500',
                    loop.type === 'condenser-water' && 'bg-purple-500',
                    loop.type === 'ground-loop' && 'bg-green-500',
                    loop.type === 'service-hot-water' && 'bg-orange-500'
                  )}
                />
                <div>
                  <h4 className="font-medium text-sm">{loopTypeNames[loop.type] || loop.type}</h4>
                  <p className="text-xs text-muted-foreground">
                    Equipment: {equipmentNames[loop.equipment] || loop.equipment}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Step 4: Review and Apply
 */
function ReviewStep() {
  const template = useSelectedTemplate();
  const parameterValues = useParameterValues();
  const zoneSelections = useZoneSelections();
  const selectedZones = zoneSelections.filter((z) => z.selected);

  const preview = useMemo(() => {
    if (!template) return null;
    const systemName = String(parameterValues['systemName'] || template.name);
    return generateSystemPreview(template, systemName, parameterValues, selectedZones);
  }, [template, parameterValues, selectedZones]);

  if (!template || !preview) return null;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="border rounded-lg p-4">
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Components</h4>
          <div className="text-2xl font-bold">{preview.nodes.length}</div>
          <p className="text-xs text-muted-foreground">HVAC components will be created</p>
        </div>
        <div className="border rounded-lg p-4">
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Connections</h4>
          <div className="text-2xl font-bold">{preview.edges.length}</div>
          <p className="text-xs text-muted-foreground">Fluid connections will be made</p>
        </div>
        <div className="border rounded-lg p-4">
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Zones</h4>
          <div className="text-2xl font-bold">{selectedZones.length}</div>
          <p className="text-xs text-muted-foreground">Zones will be served</p>
        </div>
        <div className="border rounded-lg p-4">
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Plant Loops</h4>
          <div className="text-2xl font-bold">{preview.plantLoops.length}</div>
          <p className="text-xs text-muted-foreground">Plant loops will be created</p>
        </div>
      </div>

      {/* Component List */}
      <div>
        <h4 className="text-sm font-medium mb-2">Components to Create</h4>
        <div className="border rounded-lg divide-y max-h-48 overflow-auto">
          {preview.nodes.map((node) => (
            <div key={node.id} className="p-2 text-sm flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-primary/60" />
              <span>{node.name}</span>
              <span className="text-xs text-muted-foreground">({node.componentType})</span>
            </div>
          ))}
        </div>
      </div>

      {/* Plant Loops */}
      {preview.plantLoops.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-2">Plant Loops to Create</h4>
          <div className="border rounded-lg divide-y">
            {preview.plantLoops.map((loop, index) => (
              <div key={index} className="p-2 text-sm flex items-center gap-2">
                <div
                  className={cn(
                    'w-2 h-2 rounded-full',
                    loop.type === 'chilled-water' && 'bg-blue-500',
                    loop.type === 'hot-water' && 'bg-red-500',
                    loop.type === 'condenser-water' && 'bg-purple-500'
                  )}
                />
                <span>{loop.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Template Wizard component
 */
export function TemplateWizard() {
  const wizard = useWizardState();
  const template = useSelectedTemplate();
  const parameterValues = useParameterValues();
  const zoneSelections = useZoneSelections();
  const selectedZones = zoneSelections.filter((z) => z.selected);

  const nextStep = useTemplateStore((s) => s.nextStep);
  const previousStep = useTemplateStore((s) => s.previousStep);
  const cancelWizard = useTemplateStore((s) => s.cancelWizard);
  const resetWizard = useTemplateStore((s) => s.resetWizard);
  const isStepComplete = useTemplateStore((s) => s.isStepComplete);
  const setLastResult = useTemplateStore((s) => s.setLastResult);

  const [isApplying, setIsApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);

  const canProceed = isStepComplete(wizard.currentStep);

  const handleApply = async () => {
    if (!template) return;

    setIsApplying(true);
    setApplyError(null);

    try {
      const systemName = String(parameterValues['systemName'] || template.name);
      const result = applyTemplate(template, systemName, parameterValues, selectedZones);

      setLastResult(result);

      if (result.success) {
        resetWizard();
      } else {
        setApplyError(result.error || 'Failed to apply template');
      }
    } catch (error) {
      setApplyError(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsApplying(false);
    }
  };

  if (!template) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <p>No template selected. Choose a template from the library.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b">
        <StepIndicator />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4">
        {wizard.currentStep === 'select-template' && (
          <div className="text-center py-8">
            <p className="text-muted-foreground">Template selected. Click Next to continue.</p>
          </div>
        )}
        {wizard.currentStep === 'configure-system' && <ConfigureSystemStep />}
        {wizard.currentStep === 'assign-zones' && <AssignZonesStep />}
        {wizard.currentStep === 'plant-loops' && <PlantLoopsStep />}
        {wizard.currentStep === 'review' && <ReviewStep />}

        {/* Error Display */}
        {applyError && (
          <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
            {applyError}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t flex items-center justify-between">
        <Button variant="outline" onClick={cancelWizard}>
          Cancel
        </Button>

        <div className="flex gap-2">
          {wizard.currentStep !== 'configure-system' && (
            <Button variant="outline" onClick={previousStep}>
              Back
            </Button>
          )}

          {wizard.currentStep === 'review' ? (
            <Button onClick={handleApply} disabled={!canProceed || isApplying}>
              {isApplying ? 'Applying...' : 'Apply Template'}
            </Button>
          ) : (
            <Button onClick={nextStep} disabled={!canProceed}>
              Next
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default TemplateWizard;
